import { useRef, useEffect } from 'react';
import ChatMessage from './ChatMessage';
import TypingIndicator from './TypingIndicator';
import '../../styles/ChatDrawer.css';

/**
 * ChatDrawerView — presentational chat drawer.
 *
 * Driven entirely by a `controller` object (see useChat / useDemoChat). The view
 * owns only DOM refs and the isOpen-gated scroll/focus effects; all chat state and
 * behavior comes from the controller, so prod and demo share identical rendering.
 *
 * Generic divergence points the controller configures:
 *   - suggestions[] + onSuggestion + suggestionsAlwaysVisible (prod: populate input
 *     in the empty state; demo: a persistent button-driven action bar)
 *   - inputLocked / showSendButton (demo locks free-text to button-only)
 */
function SuggestionIcon({ icon }) {
  if (!icon) return null;
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      {icon === 'trend' && <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />}
      {icon === 'alert' && <><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></>}
      {icon === 'lifestyle' && <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z" />}
    </svg>
  );
}

function Suggestions({ controller, textareaRef }) {
  const { suggestions = [], onSuggestion, pulseSuggestions, busy } = controller;
  if (!suggestions.length) return null;
  return (
    <div className="chat-suggestions">
      {suggestions.map(s => (
        <button
          key={s.id || s.label}
          // Demo affordance: strobe the actionable chips (not the disabled teaser) until
          // the first question is clicked. Prod's controller leaves pulseSuggestions
          // undefined → no pulse.
          className={`chat-suggestion-chip${s.disabled ? ' disabled' : ''}${pulseSuggestions && !s.disabled ? ' demo-pulse' : ''}`}
          // Disabled while a turn is animating (busy) so a second question can't be clicked.
          disabled={s.disabled || busy}
          title={s.tooltip || ''}
          onClick={() => {
            if (s.disabled || busy) return;
            onSuggestion?.(s);
            textareaRef.current?.focus();
          }}
        >
          <SuggestionIcon icon={s.icon} />
          {s.label}
        </button>
      ))}
    </div>
  );
}

export default function ChatDrawerView({ controller, isOpen, onToggle, fullscreen, onFullscreenToggle, patientName }) {
  const {
    messages, loading, error, inputValue, setInputValue,
    isPatientMode, showModelSelect, modelList, selectedModelIndex, setSelectedModelIndex,
    modelWarning, suggestionsAlwaysVisible, inputLocked, placeholder, showSendButton,
    canSend, emptyStateText, send,
  } = controller;

  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  // Auto-scroll to bottom on new messages (only when drawer is open).
  useEffect(() => {
    if (!isOpen) return;
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading, isOpen]);

  // Focus textarea when drawer opens (no-op when input is locked).
  useEffect(() => {
    if (isOpen && !inputLocked) {
      setTimeout(() => textareaRef.current?.focus(), 300);
    }
  }, [isOpen, inputLocked]);

  function handleKeyDown(e) {
    if (inputLocked) return;
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  }

  const emptyText = emptyStateText ?? (isPatientMode
    ? 'Ask Elis about your lab results, trends, or health scores.'
    : `Ask about ${patientName || 'this patient'}’s lab results, trends, or health scores.`);

  const showEmpty = messages.length === 0 && !loading;

  return (
    <div className={`chat-drawer ${isOpen ? 'open' : 'closed'}`}>
      <div className="chat-drawer-header">
        <h3>Elis</h3>
        {showModelSelect && (
          <select
            className="chat-model-select"
            value={selectedModelIndex}
            onChange={(e) => setSelectedModelIndex(Number(e.target.value))}
            disabled={loading}
            title={modelList[selectedModelIndex]?.warning || ''}
          >
            {modelList.map((opt, i) => (
              <option key={`${opt.provider}-${opt.model}`} value={i} title={opt.warning || ''}>{opt.label}</option>
            ))}
          </select>
        )}
        {onFullscreenToggle && (
          <button className="chat-drawer-fullscreen" onClick={onFullscreenToggle} title={fullscreen ? 'Exit fullscreen' : 'Fullscreen'}>
            {fullscreen ? (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="4 14 10 14 10 20" />
                <polyline points="20 10 14 10 14 4" />
                <line x1="14" y1="10" x2="21" y2="3" />
                <line x1="3" y1="21" x2="10" y2="14" />
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 3 21 3 21 9" />
                <polyline points="9 21 3 21 3 15" />
                <line x1="21" y1="3" x2="14" y2="10" />
                <line x1="3" y1="21" x2="10" y2="14" />
              </svg>
            )}
          </button>
        )}
        <button className="chat-drawer-close" onClick={onToggle} title="Close chat">
          &times;
        </button>
      </div>

      {modelWarning && (
        <div className="chat-model-warning">{modelWarning}</div>
      )}

      <div className="chat-messages">
        {showEmpty && (
          <div className="chat-empty">
            <p>{emptyText}</p>
            {!suggestionsAlwaysVisible && <Suggestions controller={controller} textareaRef={textareaRef} />}
          </div>
        )}

        {messages.map((msg, i) => (
          <ChatMessage key={i} {...msg} />
        ))}

        {loading && <TypingIndicator />}

        {error && (
          <div className="chat-error">{error}</div>
        )}

        {/* Kept mounted regardless of `loading` so the demo strobe doesn't restart
            out of phase with the other pulsing affordances when Elis is "thinking". */}
        {suggestionsAlwaysVisible && (
          <Suggestions controller={controller} textareaRef={textareaRef} />
        )}

        <div ref={messagesEndRef} />
      </div>

      <div className="chat-input-area">
        <textarea
          ref={textareaRef}
          className="chat-input"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          rows={1}
          maxLength={5000}
          readOnly={inputLocked}
          // Not disabled when merely locked, so the demo's readOnly box shows the typed
          // text in normal color (it's still readOnly, so the user can't free-type).
          disabled={loading}
        />
        {showSendButton && (
          <button
            className="chat-send"
            onClick={send}
            disabled={!canSend}
            title="Send message"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="22" y1="2" x2="11" y2="13" />
              <polygon points="22 2 15 22 11 13 2 9 22 2" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}
